// Section 22 overlays owned by other streams, mounted here by the M6 integration. The overlay
// host renders the event and ledger slots inside its dialog (focus trap, Esc); each slot reads
// the store itself and closes through store actions (closeOverlay, answerPrompt, ...).
//   shortage -> ShortageDialog from src/ui/shortage/shortage-dialog.tsx (stream A). Shown while
//              the season is paused; it is its own full-screen dialog (focus trap, Esc asks
//              before discarding), so app.tsx renders it without the host (M6-02).
//   event    -> the open prompt ui.promptId routed by kind: justice cases to JusticePrompt
//              (src/ui/society/justice/justice-prompt.tsx, stream E1), pairings to the Society
//              tab's PairingsPanel (stream D), everything else to EventPrompt
//              (src/ui/events/event-prompt.tsx, stream E2), which lists every event prompt.
//   ledger   -> LedgerScreen from src/ui/ledger/ledger-screen.tsx (stream G); also the win and
//              loss screen. Export downloads the shareable run; Back returns to the final report
//              once the run has ended, else closes.
import type { ComponentType } from 'preact';
import {
  answerJustice,
  answerPairing,
  cfgOption,
  closeOverlay,
  draft,
  exportRun,
  game,
  openOverlay,
  runConfig,
  status,
  ui,
} from '../../store';
import { justiceVM } from '../../viewModels/justice';
import { jobLabel } from '../../viewModels/people';
import { societyVM } from '../../viewModels/society';
import { EventPrompt } from '../events/event-prompt';
import { LedgerScreen } from '../ledger/ledger-screen';
import { ShortageDialog } from '../shortage/shortage-dialog';
import { JusticePrompt } from '../society/justice/justice-prompt';
import { PairingsPanel } from '../society/family-panel';
import { eventsText, justiceText, societyText } from '../text/en';
import { downloadFile } from './download';

export interface OverlaySlots {
  shortage: ComponentType;
  event: ComponentType;
  ledger: ComponentType;
}

function ShortageSlot() {
  return <ShortageDialog {...cfgOption()} />;
}

/** One justice case from the report or the header (Section 13 "ask each case"). */
function JusticeCaseSlot({ promptId }: { promptId: string }) {
  const g = game.value;
  if (g === null) return null;
  const vm = justiceVM(g, draft.value, runConfig());
  const c = vm.pending.find((p) => p.promptId === promptId);
  return (
    <section class="justice" aria-labelledby="justice-prompt-title">
      <h2 id="justice-prompt-title">{justiceText.pendingTitle}</h2>
      {c === undefined ? (
        <p class="justice-muted">{justiceText.noPending}</p>
      ) : (
        <JusticePrompt vm={c} disabled={status.value !== 'deciding'} onAnswer={answerJustice} />
      )}
      <footer class="overlay__actions">
        <button type="button" class="button button--primary" onClick={() => closeOverlay()}>
          {eventsText.done}
        </button>
      </footer>
    </section>
  );
}

/** Pairings awaiting approval (Section 15), answered here or on the Society tab. */
function PairingSlot() {
  const g = game.value;
  if (g === null) return null;
  const vm = societyVM(g, draft.value, runConfig());
  return (
    <section class="society" aria-labelledby="pairing-prompt-title">
      <h2 id="pairing-prompt-title">{societyText.pairings}</h2>
      <PairingsPanel
        rows={vm.pairings}
        canEdit={status.value === 'deciding'}
        jobLabel={jobLabel}
        onAnswer={(id, approve) => answerPairing(id, approve)}
      />
      <footer class="overlay__actions">
        <button type="button" class="button button--primary" onClick={() => closeOverlay()}>
          {eventsText.done}
        </button>
      </footer>
    </section>
  );
}

/** Route the prompt the overlay was opened for by its kind (Section 19, 13, 15). */
function PromptSlot() {
  const id = ui.value.promptId;
  const prompt = id === null ? undefined : game.value?.openPrompts.find((p) => p.id === id);
  if (prompt?.kind === 'justice') return <JusticeCaseSlot promptId={prompt.id} />;
  if (prompt?.kind === 'pairing') return <PairingSlot />;
  return <EventPrompt />;
}

const exportAndDownload = (): void => {
  const file = exportRun({ shareable: true });
  if (file !== undefined) downloadFile(file);
};

/** Section 20 and 21: the Ledger, also the win and loss screen. */
function LedgerSlot() {
  return (
    <LedgerScreen
      onExport={exportAndDownload}
      onBack={() => (status.value === 'ended' ? openOverlay('report') : closeOverlay())}
    />
  );
}

export const overlayComponents: OverlaySlots = {
  shortage: ShortageSlot,
  event: PromptSlot,
  ledger: LedgerSlot,
};
